import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import { toastError, toastSuccess } from '@/lib/toast';
import type {
    HotspotDTO,
    HotspotCampoDTO,
    HotspotRestricaoDTO,
    HotspotCampoTipo,
    HotspotCampoFormato,
    HotspotCampoPapel,
    HotspotAcessoDTO,
} from '@webip/shared/src/dto/hotspot';
import type { RadiusUserGroupDTO } from '@webip/shared/src/dto/radius_user';
import HotspotPreview from './HotspotPreview';
import Icon from '@mdi/react';
import { mdiPlus, mdiTrashCan, mdiLoading, mdiArrowUp, mdiArrowDown, mdiCircle } from '@mdi/js';
import CampoMultiTexto from '@/components/Formulario/CampoMultiTexto';

type Props = {
    hotspot?: HotspotDTO | null;
    show: boolean;
    onClose: () => void;
};

type Tab = 'aparencia' | 'formulario' | 'restricoes' | 'previa' | 'acessos';

const tabs: { id: Tab; label: string }[] = [
    { id: 'aparencia', label: 'Aparência' },
    { id: 'formulario', label: 'Formulário' },
    { id: 'restricoes', label: 'Restrições' },
    { id: 'previa', label: 'Prévia' },
    { id: 'acessos', label: 'Acessos' },
];

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const TIPO_OPTIONS: { id: HotspotCampoTipo; label: string }[] = [
    { id: 'texto', label: 'Texto' },
    { id: 'numero', label: 'Número' },
    { id: 'data', label: 'Data' },
    { id: 'senha', label: 'Senha' },
    { id: 'lista', label: 'Lista' },
    { id: 'booleano', label: 'Booleano (checkbox)' },
];

// Formatos por tipo (espelha CAMPO_FORMATOS_POR_TIPO do backend); o primeiro é o padrão.
// "texto": como o valor é validado/normalizado. "lista": widget de escolha das opções.
const FORMATO_OPTIONS: Partial<Record<HotspotCampoTipo, { id: HotspotCampoFormato; label: string }[]>> = {
    texto: [
        { id: 'livre', label: 'Livre' },
        { id: 'cpf', label: 'CPF' },
        { id: 'telefone', label: 'Telefone' },
        { id: 'email', label: 'E-mail' },
    ],
    lista: [
        { id: 'selecao', label: 'Seleção (lista suspensa)' },
        { id: 'opcoes', label: 'Opções (botões)' },
    ],
};
const formatosDoTipo = (tipo: HotspotCampoTipo) => FORMATO_OPTIONS[tipo] ?? [];

// Só para tipo "texto": função do campo no portal. "nenhum" = dado extra (vai para metadados).
const PAPEL_NENHUM = 'nenhum';
const PAPEL_OPTIONS: { id: HotspotCampoPapel | typeof PAPEL_NENHUM; label: string }[] = [
    { id: PAPEL_NENHUM, label: 'Nenhum' },
    { id: 'usuario', label: 'Usuário (login)' },
    { id: 'nome', label: 'Nome de exibição' },
];

const defaultHotspot = (): Partial<HotspotDTO> => ({
    nome: '',
    lan_id: 0,
    cor_primaria: '#3b82f6',
    cor_secundaria: '#2563eb',
    cor_fundo: '#f1f5f9',
    cor_texto: '#1e293b',
    descricao: '',
    horas_dia: 0,
    campos: [],
    restricoes: [],
    ativo: true,
    permite_autocadastro: false,
});

const defaultCampo = (): Partial<HotspotCampoDTO> => ({
    nome: '',
    tipo: 'texto' as HotspotCampoTipo,
    formato: 'livre',
    papel: null,
    unico: false,
    obrigatorio: true,
    ordem: 0,
});

const defaultRestricao = (): Partial<HotspotRestricaoDTO> => ({
    hora_inicio: '08:00',
    hora_fim: '22:00',
    dias_semana: [1, 2, 3, 4, 5],
    descricao: '',
});

const HotspotConfigModal = ({ hotspot, show, onClose }: Props) => {
    const qc = useQueryClient();
    const [activeTab, setActiveTab] = useState<Tab>('aparencia');
    const [model, setModel] = useState<Partial<HotspotDTO>>(hotspot ?? defaultHotspot());
    const logoInputRef = useRef<HTMLInputElement>(null);
    const fundoInputRef = useRef<HTMLInputElement>(null);
    const termosInputRef = useRef<HTMLInputElement>(null);

    // Reset model when hotspot prop or show changes
    useEffect(() => {
        if (show) {
            setActiveTab('aparencia');
            setModel(hotspot ?? defaultHotspot());
        }
    }, [show, hotspot]);

    // ── GET config (only when editing existing hotspot) ──────────────────────
    const queryConfig = useQuery(
        ['hotspot-config', hotspot?.id],
        async () => {
            const res = await axios.get<{ ok: boolean; result: HotspotDTO }>(
                `/api/hotspot/${hotspot!.id}`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        {
            enabled: show && !!hotspot?.id,
            retry: false,
            onSuccess: (data) => setModel(data ?? defaultHotspot()),
            onError: () => setModel(hotspot ?? defaultHotspot()),
        },
    );

    // ── GET grupos RADIUS (destino do autocadastro) ──────────────────────────
    const queryGrupos = useQuery(
        ['radius-groups'],
        async () => {
            const res = await axios.get<{ ok: boolean; result: RadiusUserGroupDTO[] }>(
                '/api/radius/groups',
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        { enabled: show && activeTab === 'formulario' },
    );
    // Só grupos que podem autenticar num hotspot: ativos, escopo ≠ none,
    // serviço "hotspot" (ou sem restrição de serviço).
    const gruposHotspot = (queryGrupos.data ?? []).filter(
        g => g.ativo && (g.appliance_scope ?? 'all') !== 'none' && (!g.servicos?.length || g.servicos.includes('hotspot')),
    );
    // Sem grupo escolhido, o backend usa o grupo padrão do tenant (se elegível).
    const grupoPadrao = gruposHotspot.find(g => g.is_default);

    // ── GET acessos ─────────────────────────────────────────────────────────
    const queryAcessos = useQuery(
        ['hotspot-acessos', model.id],
        async () => {
            const res = await axios.get(
                `/api/hotspot/${model.id}/acessos?limit=50`,
                { withCredentials: true },
            );
            return res.data.result as HotspotAcessoDTO[];
        },
        // Recarrega a cada minuto: "online" depende do keepalive do router (3 min).
        { enabled: show && activeTab === 'acessos' && !!model.id, refetchInterval: 60_000 },
    );


    // ── POST / PUT config ────────────────────────────────────────────────────
    const mutSave = useMutation({
        mutationFn: async () => {
            if (model.id) {
                await axios.put(`/api/hotspot/${model.id}`, model, { withCredentials: true });
                return { isNew: false };
            } else {
                const res = await axios.post<{ ok: boolean; result: HotspotDTO }>(
                    `/api/hotspot`, model, { withCredentials: true }
                );
                setModel(res.data.result);
                return { isNew: true };
            }
        },
        onSuccess: (data) => {
            toastSuccess('Configuração do hotspot salva!');
            qc.invalidateQueries(['hotspot-list']);
            if (data?.isNew) {
                onClose();
            }
        },
        onError: (err: any) => {
            const msg = err?.response?.data?.message ?? 'Erro ao salvar';
            toastError(msg);
        },
    });

    // ── POST logo ────────────────────────────────────────────────────────────
    const mutLogo = useMutation({
        mutationFn: async (file: File) => {
            const fd = new FormData();
            fd.append('logo', file);
            const res = await axios.post(`/api/hotspot/${model.id}/logo`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return res.data.result as { logo_url: string };
        },
        onSuccess: (data) => {
            setModel(prev => ({ ...prev, logo_url: `${data.logo_url}?t=${Date.now()}` }));
            toastSuccess('Logo atualizado!');
        },
        onError: () => toastError('Erro ao fazer upload do logo'),
    });

    // ── POST fundo ─────────────────────────────────────────────────────────
    const mutFundo = useMutation({
        mutationFn: async (file: File) => {
            const fd = new FormData();
            fd.append('fundo', file);
            const res = await axios.post(`/api/hotspot/${model.id}/fundo`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return res.data.result as { fundo_url: string };
        },
        onSuccess: (data) => {
            setModel(prev => ({ ...prev, fundo_url: `${data.fundo_url}?t=${Date.now()}` }));
            toastSuccess('Imagem de fundo atualizada!');
        },
        onError: () => toastError('Erro ao fazer upload do fundo'),
    });

    // ── DELETE logo ───────────────────────────────────────────────────────
    const mutRemoveLogo = useMutation({
        mutationFn: async () => {
            await axios.delete(`/api/hotspot/${model.id}/logo`, { withCredentials: true });
        },
        onSuccess: () => {
            setModel(prev => ({ ...prev, logo_url: null }));
            toastSuccess('Logo removido!');
        },
        onError: () => toastError('Erro ao remover logo'),
    });

    // ── DELETE fundo ──────────────────────────────────────────────────────
    const mutRemoveFundo = useMutation({
        mutationFn: async () => {
            await axios.delete(`/api/hotspot/${model.id}/fundo`, { withCredentials: true });
        },
        onSuccess: () => {
            setModel(prev => ({ ...prev, fundo_url: null }));
            toastSuccess('Imagem de fundo removida!');
        },
        onError: () => toastError('Erro ao remover fundo'),
    });

    // ── POST termos (PDF) ─────────────────────────────────────────────────
    const mutTermos = useMutation({
        mutationFn: async (file: File) => {
            const fd = new FormData();
            fd.append('termos', file);
            const res = await axios.post(`/api/hotspot/${model.id}/termos`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return res.data.result as { termos_url: string };
        },
        onSuccess: (data) => {
            setModel(prev => ({ ...prev, termos_url: `${data.termos_url}?t=${Date.now()}` }));
            toastSuccess('Termos de uso atualizados!');
        },
        onError: () => toastError('Erro ao fazer upload dos termos de uso'),
    });

    // ── DELETE termos ─────────────────────────────────────────────────────
    const mutRemoveTermos = useMutation({
        mutationFn: async () => {
            await axios.delete(`/api/hotspot/${model.id}/termos`, { withCredentials: true });
        },
        onSuccess: () => {
            setModel(prev => ({ ...prev, termos_url: null }));
            toastSuccess('Termos de uso removidos!');
        },
        onError: () => toastError('Erro ao remover termos de uso'),
    });

    const addCampo = () => {
        const novo = { ...defaultCampo(), id: Date.now(), hotspot_id: model.id ?? 0, ordem: (model.campos?.length ?? 0) } as HotspotCampoDTO;
        setModel(prev => ({ ...prev, campos: [...(prev.campos ?? []), novo] }));
    };

    // `ordem` é sempre a posição na lista — é o que o backend persiste e o
    // que o portal e a prévia respeitam.
    const reindexar = (campos: HotspotCampoDTO[]) => campos.map((c, i) => ({ ...c, ordem: i }));

    const removeCampo = (idx: number) => {
        setModel(prev => ({ ...prev, campos: reindexar((prev.campos ?? []).filter((_, i) => i !== idx)) }));
    };

    const moveCampo = (idx: number, delta: -1 | 1) => {
        setModel(prev => {
            const campos = [...(prev.campos ?? [])];
            const alvo = idx + delta;
            if (alvo < 0 || alvo >= campos.length) return prev;
            [campos[idx], campos[alvo]] = [campos[alvo], campos[idx]];
            return { ...prev, campos: reindexar(campos) };
        });
    };

    const updateCampo = (idx: number, patch: Partial<HotspotCampoDTO>) => {
        setModel(prev => ({
            ...prev,
            campos: prev.campos?.map((c, i) => {
                // Um só campo de login por hotspot: marcar um desmarca o outro
                if (i !== idx) return patch.papel === 'usuario' && c.papel === 'usuario' ? { ...c, papel: null } : c;
                const novo = { ...c, ...patch };
                // formato depende do tipo (texto/lista); papel só existe em "texto"
                const formatos = formatosDoTipo(novo.tipo);
                if (!formatos.some(f => f.id === novo.formato)) novo.formato = formatos[0]?.id ?? null;
                if (novo.tipo !== 'texto') novo.papel = null;
                return novo;
            }),
        }));
    };

    const addRestricao = () => {
        const nova = { ...defaultRestricao(), id: Date.now(), hotspot_id: model.id ?? 0 } as HotspotRestricaoDTO;
        setModel(prev => ({ ...prev, restricoes: [...(prev.restricoes ?? []), nova] }));
    };

    const removeRestricao = (idx: number) => {
        setModel(prev => ({ ...prev, restricoes: prev.restricoes?.filter((_, i) => i !== idx) }));
    };

    const updateRestricao = (idx: number, patch: Partial<HotspotRestricaoDTO>) => {
        setModel(prev => ({
            ...prev,
            restricoes: prev.restricoes?.map((r, i) => i === idx ? { ...r, ...patch } : r),
        }));
    };

    const toggleDia = (restricaoIdx: number, dia: number) => {
        const r = model.restricoes?.[restricaoIdx];
        if (!r) return;
        const dias = r.dias_semana ?? [];
        const next = dias.includes(dia) ? dias.filter(d => d !== dia) : [...dias, dia];
        updateRestricao(restricaoIdx, { dias_semana: next });
    };

    const tabStyle = (id: Tab): React.CSSProperties => ({
        padding: '8px 14px',
        borderRadius: '6px 6px 0 0',
        border: 'none',
        background: activeTab === id ? 'var(--tc-color-primary)' : 'transparent',
        color: activeTab === id ? 'var(--fg-inverse)' : 'var(--color-body)',
        fontWeight: activeTab === id ? 600 : 400,
        cursor: 'pointer',
        fontSize: '13px',
        transition: 'background 0.15s',
    });

    const showSaveControls = activeTab === 'aparencia' || activeTab === 'formulario' || activeTab === 'restricoes';

    const handleConfirm = () => {
        if (!model.nome?.trim()) {
            toastError('O campo "Nome do hotspot" é obrigatório');
            return;
        }
        mutSave.mutate();
    };

    return (
        <Modal
            show={show}
            title={model.id ? `Hotspot — ${model.nome}` : `Novo Hotspot`}
            onClose={onClose}
            onCancel={onClose}
            onConfirm={handleConfirm}
            confirmProps={{ visible: showSaveControls, disabled: mutSave.isLoading, label: mutSave.isLoading ? 'Salvando…' : 'Salvar' }}
            cancelProps={{ visible: showSaveControls, label: 'Cancelar' }}
            size="lg"
        >
            {/* Tabs */}
            <HBox style={{ borderBottom: '1px solid var(--border-1)', marginBottom: '16px', flexWrap: 'wrap', gap: '2px' }}>
                {tabs.map(t => (
                    <button key={t.id} type="button" onClick={() => setActiveTab(t.id)} style={tabStyle(t.id)}>
                        {t.label}
                    </button>
                ))}
            </HBox>

            {/* ── Tab: Aparência ──────────────────────────────────────────────── */}
            {activeTab === 'aparencia' && (
                <VBox style={{ gap: '12px' }}>
                    <CampoTexto
                        label="Nome do hotspot"
                        value={model.nome ?? ''}
                        onChange={v => setModel(p => ({ ...p, nome: v }))}
                        hint="Identificador deste hotspot (ex: Hotspot Recepção)"
                    />
                    <CampoTexto
                        label="Descrição"
                        value={model.descricao ?? ''}
                        onChange={v => setModel(p => ({ ...p, descricao: v }))}
                    />
                    <HBox style={{ gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '140px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Cor primária</label>
                            <input type="color" value={model.cor_primaria ?? '#3b82f6'} onChange={e => setModel(p => ({ ...p, cor_primaria: e.target.value }))} style={{ width: '100%', height: '36px', borderRadius: '6px', border: '1px solid var(--border-1)', cursor: 'pointer' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: '140px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Cor secundária</label>
                            <input type="color" value={model.cor_secundaria ?? '#2563eb'} onChange={e => setModel(p => ({ ...p, cor_secundaria: e.target.value }))} style={{ width: '100%', height: '36px', borderRadius: '6px', border: '1px solid var(--border-1)', cursor: 'pointer' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: '140px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Cor de fundo</label>
                            <input type="color" value={model.cor_fundo ?? '#f1f5f9'} onChange={e => setModel(p => ({ ...p, cor_fundo: e.target.value }))} style={{ width: '100%', height: '36px', borderRadius: '6px', border: '1px solid var(--border-1)', cursor: 'pointer' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: '140px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Cor do texto</label>
                            <input type="color" value={model.cor_texto ?? '#1e293b'} onChange={e => setModel(p => ({ ...p, cor_texto: e.target.value }))} style={{ width: '100%', height: '36px', borderRadius: '6px', border: '1px solid var(--border-1)', cursor: 'pointer' }} />
                        </div>
                    </HBox>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Logo</label>
                        {model.logo_url && (
                            <img src={model.logo_url} alt="Logo atual" style={{ maxHeight: '60px', maxWidth: '200px', marginBottom: '8px', display: 'block', borderRadius: '6px', border: '1px solid var(--border-1)' }} />
                        )}
                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}
                            onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) mutLogo.mutate(file);
                            }}
                        />
                        <Botao
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={mutLogo.isLoading || !model.id}
                        >{mutLogo.isLoading ? 'Enviando…' : model.logo_url ? 'Alterar logo' : 'Adicionar logo'}</Botao>
                        {model.logo_url && (
                            <Botao
                                variant="outline-danger"
                                size="sm"
                                onClick={() => mutRemoveLogo.mutate()}
                                disabled={mutRemoveLogo.isLoading}
                                style={{ marginLeft: '8px' }}
                            >{mutRemoveLogo.isLoading ? 'Removendo…' : 'Remover logo'}</Botao>
                        )}
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Imagem de fundo</label>
                        {model.fundo_url && (
                            <img src={model.fundo_url} alt="Fundo atual" style={{ maxHeight: '80px', maxWidth: '100%', marginBottom: '8px', display: 'block', borderRadius: '6px', border: '1px solid var(--border-1)', objectFit: 'cover' }} />
                        )}
                        <input
                            ref={fundoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}
                            onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) mutFundo.mutate(file);
                            }}
                        />
                        <Botao
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => fundoInputRef.current?.click()}
                            disabled={mutFundo.isLoading || !model.id}
                        >{mutFundo.isLoading ? 'Enviando…' : model.fundo_url ? 'Alterar fundo' : 'Adicionar fundo'}</Botao>
                        {model.fundo_url && (
                            <Botao
                                variant="outline-danger"
                                size="sm"
                                onClick={() => mutRemoveFundo.mutate()}
                                disabled={mutRemoveFundo.isLoading}
                                style={{ marginLeft: '8px' }}
                            >{mutRemoveFundo.isLoading ? 'Removendo…' : 'Remover fundo'}</Botao>
                        )}
                        {model.fundo_url && (
                            <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--color-muted)' }}>Sobrepõe a cor de fundo quando definida</span>
                        )}
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Termos de uso (PDF)</label>
                        {model.termos_url && (
                            <a
                                href={model.termos_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: 'var(--tc-color-primary)' }}
                            >
                                Ver PDF atual
                            </a>
                        )}
                        <input
                            ref={termosInputRef}
                            type="file"
                            accept="application/pdf"
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}
                            onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) mutTermos.mutate(file);
                            }}
                        />
                        <Botao
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => termosInputRef.current?.click()}
                            disabled={mutTermos.isLoading || !model.id}
                        >{mutTermos.isLoading ? 'Enviando…' : model.termos_url ? 'Alterar PDF' : 'Adicionar PDF'}</Botao>
                        {model.termos_url && (
                            <Botao
                                variant="outline-danger"
                                size="sm"
                                onClick={() => mutRemoveTermos.mutate()}
                                disabled={mutRemoveTermos.isLoading}
                                style={{ marginLeft: '8px' }}
                            >{mutRemoveTermos.isLoading ? 'Removendo…' : 'Remover PDF'}</Botao>
                        )}
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: 'var(--color-muted)' }}>
                            Quando definido, o portal exibe um checkbox obrigatório "Li e aceito os termos de uso" com link para este PDF.
                        </p>
                    </div>
                    <CampoCheckbox
                        label="Hotspot ativo"
                        value={model.ativo ?? true}
                        onChange={v => setModel(p => ({ ...p, ativo: v }))}
                    />
                </VBox>
            )}

            {/* ── Tab: Formulário ─────────────────────────────────────────────── */}
            {activeTab === 'formulario' && (
                <VBox style={{ gap: '10px' }}>
                    {(model.campos ?? []).map((campo, idx) => (
                        <VBox key={idx} style={{ gap: '10px', padding: '10px', borderRadius: '8px', background: 'var(--bg-3)', border: '1px solid var(--border-1)' }}>
                            <HBox style={{ gap: '10px', alignItems: 'flex-end' }}>
                                <div style={{ flex: 2 }}>
                                    <CampoTexto
                                        label="Nome do campo"
                                        value={campo.nome}
                                        onChange={v => updateCampo(idx, { nome: v })}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <CampoSeletor
                                        label="Tipo"
                                        value={campo.tipo}
                                        onChange={v => updateCampo(idx, { tipo: v as HotspotCampoTipo })}
                                        options={TIPO_OPTIONS}
                                    />
                                </div>
                                {formatosDoTipo(campo.tipo).length > 0 && (
                                    <div style={{ flex: 1 }}>
                                        <CampoSeletor
                                            label="Formato"
                                            value={campo.formato ?? formatosDoTipo(campo.tipo)[0].id}
                                            onChange={v => updateCampo(idx, { formato: v as HotspotCampoFormato })}
                                            options={formatosDoTipo(campo.tipo)}
                                        />
                                    </div>
                                )}
                                {campo.tipo === 'texto' && (
                                    <div style={{ flex: 1 }}>
                                        <CampoSeletor
                                            label="Papel"
                                            value={campo.papel ?? PAPEL_NENHUM}
                                            onChange={v => updateCampo(idx, { papel: v && v !== PAPEL_NENHUM ? (v as HotspotCampoPapel) : null })}
                                            options={PAPEL_OPTIONS}
                                        />
                                    </div>
                                )}
                                <div style={{ paddingBottom: '2px' }}>
                                    <CampoCheckbox label="Obrig." value={campo.obrigatorio} onChange={v => updateCampo(idx, { obrigatorio: v })} />
                                </div>
                                <div style={{ paddingBottom: '2px' }}>
                                    <CampoCheckbox label="Único" value={campo.unico} onChange={v => updateCampo(idx, { unico: v })} />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => moveCampo(idx, -1)}
                                    disabled={idx === 0}
                                    style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: 'var(--color-muted)', opacity: idx === 0 ? 0.35 : 1, padding: '4px', marginBottom: '2px' }}
                                    title="Mover para cima"
                                >
                                    <Icon path={mdiArrowUp} size="18px" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => moveCampo(idx, 1)}
                                    disabled={idx === (model.campos?.length ?? 0) - 1}
                                    style={{ background: 'none', border: 'none', cursor: idx === (model.campos?.length ?? 0) - 1 ? 'default' : 'pointer', color: 'var(--color-muted)', opacity: idx === (model.campos?.length ?? 0) - 1 ? 0.35 : 1, padding: '4px', marginBottom: '2px' }}
                                    title="Mover para baixo"
                                >
                                    <Icon path={mdiArrowDown} size="18px" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => removeCampo(idx)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53935', padding: '4px', marginBottom: '2px' }}
                                    title="Remover campo"
                                >
                                    <Icon path={mdiTrashCan} size="18px" />
                                </button>
                            </HBox>
                            {campo.tipo === 'lista' && (
                                <CampoMultiTexto
                                    label="Opções"
                                    value={(campo.opcoes ?? [])}
                                    onChange={v => updateCampo(idx, { opcoes: v.map(s => s.trim()).filter(Boolean) })}
                                    hint={campo.formato === 'opcoes'
                                        ? 'Valores exibidos como botões de opção (radio) no portal'
                                        : 'Valores exibidos numa lista suspensa no portal'}
                                />
                            )}
                        </VBox>
                    ))}
                    <Botao
                        variant="outline-primary"
                        size="sm"
                        label="Adicionar campo"
                        icon={mdiPlus}
                        onClick={addCampo}
                    />
                    <div style={{ marginTop: '12px', padding: '12px', borderRadius: '8px', background: 'var(--bg-3)', border: '1px solid var(--border-1)' }}>
                        <CampoCheckbox
                            label="Permitir que usuários se cadastrem no portal"
                            value={model.permite_autocadastro ?? false}
                            onChange={v => setModel(p => ({ ...p, permite_autocadastro: v }))}
                        />
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--color-muted)' }}>
                            Quando ativo, o portal exibirá um botão "Cadastre-se" para novos usuários.
                            O cadastro cria um usuário RADIUS no grupo abaixo, com os demais campos do formulário guardados junto.
                        </p>
                        {model.permite_autocadastro && (
                            <div style={{ marginTop: '10px' }}>
                                <CampoSeletor
                                    label="Grupo RADIUS dos usuários cadastrados"
                                    placeholder={
                                        queryGrupos.isLoading ? 'Carregando…'
                                        : grupoPadrao ? `Grupo padrão (${grupoPadrao.nome})`
                                        : 'Selecione um grupo'
                                    }
                                    value={model.radius_grupo_id != null ? String(model.radius_grupo_id) : null}
                                    onChange={v => setModel(p => ({ ...p, radius_grupo_id: v ? Number(v) : null }))}
                                    options={gruposHotspot.map(g => ({
                                        id: String(g.id),
                                        label: g.is_default ? `${g.nome} (padrão)` : g.nome,
                                    }))}
                                />
                                {!queryGrupos.isLoading && !grupoPadrao && model.radius_grupo_id == null && gruposHotspot.length > 0 && (
                                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--color-muted)' }}>
                                        Sem grupo padrão no tenant — selecione um grupo.
                                    </p>
                                )}
                                {!queryGrupos.isLoading && gruposHotspot.length === 0 && (
                                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--status-offline)' }}>
                                        Nenhum grupo RADIUS ativo com o serviço Hotspot. Crie um na tela RADIUS antes de habilitar o cadastro.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </VBox>
            )}

            {/* ── Tab: Restrições ─────────────────────────────────────────────── */}
            {activeTab === 'restricoes' && (
                <VBox style={{ gap: '12px' }}>
                    <CampoNumero
                        label="Horas de acesso por dia (0 = ilimitado)"
                        value={model.horas_dia ?? 0}
                        onChange={v => setModel(p => ({ ...p, horas_dia: v }))}
                        min={0}
                        max={24}
                    />
                    <div style={{ fontSize: '13px', fontWeight: 500, marginTop: '4px' }}>Janelas de horário permitidas</div>
                    {(model.restricoes ?? []).map((r, idx) => (
                        <VBox key={idx} style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--border-1)', background: 'var(--bg-3)', gap: '10px' }}>
                            <HBox style={{ gap: '12px', alignItems: 'flex-end' }}>
                                <div style={{ flex: 1 }}>
                                    <CampoTexto
                                        label="Início"
                                        value={r.hora_inicio}
                                        onChange={v => updateRestricao(idx, { hora_inicio: v })}
                                        hint="HH:MM"
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <CampoTexto
                                        label="Fim"
                                        value={r.hora_fim}
                                        onChange={v => updateRestricao(idx, { hora_fim: v })}
                                        hint="HH:MM"
                                    />
                                </div>
                                <div style={{ flex: 2 }}>
                                    <CampoTexto
                                        label="Descrição"
                                        value={r.descricao ?? ''}
                                        onChange={v => updateRestricao(idx, { descricao: v })}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeRestricao(idx)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53935', padding: '4px', marginBottom: '2px' }}
                                >
                                    <Icon path={mdiTrashCan} size="18px" />
                                </button>
                            </HBox>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '6px' }}>Dias da semana</div>
                                <HBox style={{ gap: '6px', flexWrap: 'wrap' }}>
                                    {DIAS.map((d, i) => {
                                        const active = (r.dias_semana ?? []).includes(i);
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => toggleDia(idx, i)}
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '6px',
                                                    border: '1.5px solid',
                                                    borderColor: active ? 'var(--tc-color-primary)' : 'var(--border-1)',
                                                    background: active ? 'var(--tc-color-primary)' : 'transparent',
                                                    color: active ? 'var(--fg-inverse)' : 'var(--color-body)',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    fontWeight: active ? 600 : 400,
                                                }}
                                            >
                                                {d}
                                            </button>
                                        );
                                    })}
                                </HBox>
                            </div>
                        </VBox>
                    ))}
                    <Botao
                        variant="outline-primary"
                        size="sm"
                        label="Adicionar janela"
                        icon={mdiPlus}
                        onClick={addRestricao}
                    />
                </VBox>
            )}

            {/* ── Tab: Prévia ─────────────────────────────────────────────────── */}
            {activeTab === 'previa' && (
                <HotspotPreview hotspot={model} />
            )}

            {/* ── Tab: Acessos ─────────────────────────────────────────────────── */}
            {activeTab === 'acessos' && (
                <VBox style={{ gap: '8px' }}>
                    {queryAcessos.isLoading && <Icon path={mdiLoading} size="24px" spin />}
                    {(queryAcessos.data ?? []).length === 0 && !queryAcessos.isLoading && (
                        <div style={{ color: 'var(--fg-3)', fontSize: '13px' }}>Nenhum acesso registrado.</div>
                    )}
                    {(queryAcessos.data ?? []).map((a) => {
                        const fmt = (d?: Date | string) => (d ? new Date(d).toLocaleString('pt-BR') : '—');
                        const statusTitle = a.online
                            ? `Online — último sinal ${fmt(a.ultimo_keepalive ?? a.data_acesso)}`
                            : a.data_logoff
                                ? `Logoff ${fmt(a.data_logoff)}`
                                : `Sem sinal desde ${fmt(a.ultimo_keepalive ?? a.data_acesso)}`;
                        return (
                            <HBox key={a.id} style={{ gap: '12px', alignItems: 'center', padding: '8px 12px', borderRadius: '8px', background: 'var(--bg-3)', border: '1px solid var(--border-1)', fontSize: '12px', flexWrap: 'wrap' }}>
                                <Icon path={mdiCircle} size={0.45} color={a.online ? '#16a34a' : 'var(--fg-3)'} title={statusTitle} />
                                <div style={{ flex: 1, minWidth: '80px', color: 'var(--color-body)' }}>{a.username || a.ip || '—'}</div>
                                <div style={{ flex: 1, minWidth: '80px', color: 'var(--color-body)' }}>{a.ip || '—'}</div>
                                <div style={{ flex: 1, minWidth: '80px', color: 'var(--color-body)' }}>{a.mac || '—'}</div>
                                <div style={{ flex: 2, color: 'var(--color-muted)' }}>{JSON.stringify(a.dados)}</div>
                                <div style={{ color: 'var(--fg-3)', minWidth: '120px' }} title={statusTitle}>{fmt(a.data_acesso)}</div>
                            </HBox>
                        );
                    })}
                </VBox>
            )}


        </Modal>
    );
};

export default HotspotConfigModal;
